// File: frontend/src/components/ui/ScopeChip.js
//
// STATES WHAT SCOPE A PAGE IS SHOWING. One chip, one wording, every page.
//
// WHY IT EXISTS
//   The sidebar's router selection silently narrowed several owner-wide pages.
//   Audited 17 Aug 2026, every page that reads `selectedRouterId`:
//
//     Router Dashboard · Hotspot Customers · Remote Access · Routers
//         router-scoped by nature — the page IS about one station
//     Sales · Transactions · Wallet · Agents
//         narrowed, and each already said so in its own words
//     Analytics · Packages
//         narrowed and said NOTHING — the defect this component closes
//
//   Four pages had four different ways of saying the same thing. That is how a
//   product ends up with "Selected router", "All routers", "Payments for X" and
//   "No station selected" all meaning scope. This is the one wording.
//
// WHAT IT IS NOT
//   Not a control. It states scope; it never changes it. The sidebar owns the
//   selection, and a second control for one concept is two answers to "which
//   station am I looking at". A page that needs its own filter should show a real
//   filter and label it — this chip is for the GLOBAL selection's effect.

import React from "react";
import { Chip, Tooltip } from "@mui/material";
import { TYPE_SCALE } from "./tokens";

/**
 * @param {"router"|"business"} scope  which level the page is currently showing
 * @param {string} [name]              the station's name, when scope is "router"
 * @param {string} [explain]           optional tooltip: what the scope means here
 */
export default function ScopeChip({ scope, name, explain, sx }) {
  const isRouter = scope === "router";
  const label = isRouter
    ? `Filtered to ${name || "the selected station"}`
    : "Business-wide";
  const chip = (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22, fontSize: TYPE_SCALE.label, fontWeight: 700,
        maxWidth: 260,
        ...sx,
      }}
    />
  );
  const title = explain || (isRouter
    ? "This page is showing one station only, chosen with the router selector in the sidebar."
    : "This page covers the whole business and is not affected by the router selector.");
  return <Tooltip title={title}>{chip}</Tooltip>;
}
